import { Link } from "@tanstack/react-router";
import { Facehash } from "facehash";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { capitalizeName } from "../../lib/format";

interface UserMenuProps {
	user: {
		id: string;
		name?: string | null;
		email?: string;
		image?: string | null;
		role?: string | null;
	};
	isImpersonating: boolean;
	isSigningOut: boolean;
	onSignOut: () => void;
}

export function UserMenu({
	user,
	isImpersonating,
	isSigningOut,
	onSignOut,
}: UserMenuProps) {
	const userAvatarAlt = capitalizeName(user.name) || user.email || "User";

	return (
		<div className="flex items-center gap-1.5 p-1">
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						aria-label={`User menu for ${capitalizeName(user.name) || user.email || "User"}`}
						className="flex items-center gap-2 pl-1 pr-2 py-1 transition-colors duration-200 group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{ boxShadow: "rgba(255,255,255,0.08) 0px 0px 0px 1px", borderRadius: "6px" }}
						onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.04)"; }}
						onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
						onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,255,255,0.04)"; }}
						onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
					>
						<div
							className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
							style={{ backgroundColor: "rgba(234,40,4,0.12)" }}
						>
							{user.image ? (
								<img
									src={user.image}
									alt={userAvatarAlt}
									className="h-full w-full object-cover"
									loading="lazy"
									referrerPolicy="no-referrer"
								/>
							) : (
								<Facehash name={user.email || user.id} size={32} />
							)}
						</div>
						<span
							className="hidden lg:block text-xs font-bold max-w-[120px] truncate"
							style={{ color: "#ffffff" }}
						>
							{capitalizeName(user.name) || "User"}
						</span>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px", color: "#888888" }}>expand_more</span>
					</button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="end"
					sideOffset={8}
					className="w-72 p-2"
					style={{
						backgroundColor: "#111111",
						boxShadow: "rgba(255,255,255,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.4) 0px 8px 24px 0px",
						border: "none",
						borderRadius: "8px",
					}}
				>
					<div className="px-2 py-2">
						<div
							className="flex items-center gap-3 p-2"
							style={{
								boxShadow: "rgba(255,255,255,0.06) 0px 0px 0px 1px",
								backgroundColor: "rgba(255,255,255,0.03)",
								borderRadius: "6px",
							}}
						>
							<div
								className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
								style={{ backgroundColor: "rgba(234,40,4,0.12)" }}
							>
								{user.image ? (
									<img
										src={user.image}
										alt={userAvatarAlt}
										className="h-full w-full object-cover"
										loading="lazy"
										referrerPolicy="no-referrer"
									/>
								) : (
									<Facehash name={user.email || user.id} size={40} />
								)}
							</div>
							<div className="flex flex-col min-w-0 flex-1">
								<span className="text-sm font-bold truncate" style={{ color: "#ffffff" }}>
									{capitalizeName(user.name) || "User"}
								</span>
								<span className="text-xs truncate" style={{ color: "#888888" }}>
									{user.email}
								</span>
							</div>
						</div>
					</div>

					<div className="h-px my-1" style={{ backgroundColor: "rgba(255,255,255,0.06)" }} />

					<div className="space-y-0.5">
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard"
								className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
								style={{ color: "#ffffff" }}
								onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>home</span>
								<span className="font-bold">Dashboard</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard/settings"
								className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
								style={{ color: "#ffffff" }}
								onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>settings</span>
								<span className="font-bold">Settings</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard/billing"
								search={{ checkout: undefined }}
								className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
								style={{ color: "#ffffff" }}
								onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>credit_card</span>
								<span className="font-bold">Billing</span>
							</Link>
						</DropdownMenuItem>
						<DropdownMenuItem asChild>
							<Link
								to="/dashboard/help"
								className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
								style={{ color: "#ffffff" }}
								onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>help</span>
								<span className="font-bold">Help</span>
							</Link>
						</DropdownMenuItem>
					</div>

					{user.role === "admin" && !isImpersonating && (
						<>
							<div className="h-px my-1" style={{ backgroundColor: "rgba(255,255,255,0.06)" }} />
							<div className="px-3 py-1.5">
								<span
									className="text-xs font-bold uppercase tracking-wide"
									style={{ color: "rgba(173,170,170,0.75)", fontFamily: "inherit" }}
								>
									Admin
								</span>
							</div>
							<div className="space-y-0.5">
								<DropdownMenuItem asChild>
									<Link
										to="/admin/users"
										className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
										style={{ color: "#ffffff" }}
										onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
										onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
									>
										<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>person</span>
										<span className="font-bold">Users</span>
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link
										to="/admin/feedback"
										className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
										style={{ color: "#ffffff" }}
										onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
										onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
								onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
									>
										<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>forum</span>
										<span className="font-bold">Feedback</span>
									</Link>
								</DropdownMenuItem>
							</div>
						</>
					)}

					<div className="h-px my-1" style={{ backgroundColor: "rgba(255,255,255,0.06)" }} />
					<DropdownMenuItem
						disabled={isSigningOut}
						onClick={onSignOut}
						className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors"
						style={{ color: "#ff6e84" }}
						onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,110,132,0.08)"; }}
						onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
						onFocus={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,110,132,0.08)"; }}
						onBlur={e => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
					>
						<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>logout</span>
						<span className="font-bold">Sign Out</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
